// LIVE-DESKTOP (docs/design/2026-11-03-live-desktop.md §6): hold Space to talk. While the Live
// Workspace is on screen and no text field (or button, link, dialog) has the focus, holding Space
// opens the mic through the existing voice session code (voice.ts) and releasing it ends the
// utterance (the session is stopped, which leaves the room and so closes the mic). When voice is
// not available (`voice_front` off, or the service not answering with its audio slots set) the key
// does nothing but show a short hint.
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { useSeatedDes } from "./voice-panel";
import {
  readVoiceStatus,
  startVoiceSession,
  type VoiceDeps,
  type VoiceSession,
  VoiceStartError,
} from "./voice";

/** How long a hint stays on screen. */
export const PTT_HINT_MS = 3_000;
export const PTT_UNAVAILABLE = "Voice is not available right now. Type in the chat instead.";

/**
 * Voice is offered only while the voice service answers with its audio slots set and the
 * company's `voice_front` switch is on (shared by the Text | Voice pill and hold-to-talk).
 */
export function useVoiceReady(api: Api, company: CompanyAccess) {
  const status = useQuery({
    queryKey: ["voice-panel", "status"],
    queryFn: () => readVoiceStatus(),
    retry: false,
    staleTime: 60_000,
  });
  const flag = useQuery({
    queryKey: ["live", company.id, "voice-front"],
    queryFn: () => api.client.me.morningBrief({ params: { co: company.id } }),
    select: (r) => r.voice_front === true,
    retry: false,
    staleTime: 60_000,
  });
  const ready = status.data?.voice === "configured" && flag.data === true;
  const reason = status.isPending || flag.isPending ? "Checking voice…" : "Voice is being set up";
  return { ready, reason };
}

const isTyping = (t: EventTarget | null) => {
  const el = t as HTMLElement | null;
  if (!el || typeof el.closest !== "function") return false;
  return Boolean(el.closest('input, textarea, select, [contenteditable="true"], [role="textbox"]'));
};
/** Space already means something there (press a button, follow a link, scroll a dialog). */
const ownsSpace = (t: EventTarget | null) => {
  const el = t as HTMLElement | null;
  if (!el || typeof el.closest !== "function") return false;
  return Boolean(
    el.closest('button, a[href], summary, [role="button"], [role="dialog"], [role="menu"]'),
  );
};

export type PushToTalk = { listening: boolean; hint: string | null };

/**
 * The key handling, with the session start injected (so it is testable with fakes). `begin`
 * resolves a started session, or null (with nothing to stop) when it could not start.
 */
export function usePushToTalk({
  enabled,
  ready,
  begin,
}: {
  /** False while the voice panel (its own mic) is open: Space then does nothing here. */
  enabled: boolean;
  ready: boolean;
  begin: () => Promise<VoiceSession | null>;
}): PushToTalk {
  const [listening, setListening] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const live = useRef({ ready, begin, enabled });
  live.current = { ready, begin, enabled };
  // 0 idle · 1 starting (a release now ends it as soon as it has started) · 2 on
  const phase = useRef<0 | 1 | 2>(0);
  const held = useRef(false);
  const session = useRef<VoiceSession | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const say = useCallback((text: string) => {
    setHint(text);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setHint(null), PTT_HINT_MS);
  }, []);

  const release = useCallback(() => {
    held.current = false;
    if (phase.current === 2) {
      session.current?.stop();
      session.current = null;
      phase.current = 0;
      setListening(false);
    }
  }, []);

  useEffect(() => {
    const onDown = (e: KeyboardEvent) => {
      if (e.code !== "Space" && e.key !== " ") return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      if (isTyping(e.target) || ownsSpace(e.target)) return;
      if (!live.current.enabled) return;
      if (e.repeat) {
        if (held.current) e.preventDefault();
        return;
      }
      if (!live.current.ready) {
        say(PTT_UNAVAILABLE);
        return;
      }
      e.preventDefault();
      if (phase.current !== 0) return;
      held.current = true;
      phase.current = 1;
      void live.current
        .begin()
        .then((s) => {
          if (!s) {
            phase.current = 0;
            return;
          }
          if (s.mode !== "voice") {
            s.stop();
            phase.current = 0;
            say(PTT_UNAVAILABLE);
            return;
          }
          // Let go before the mic was open: the utterance is already over.
          if (!held.current) {
            s.stop();
            phase.current = 0;
            return;
          }
          session.current = s;
          phase.current = 2;
          setListening(true);
        })
        .catch((err) => {
          phase.current = 0;
          if (!(err instanceof VoiceStartError)) say("Voice could not start.");
          else say(err.code === "voice_unreachable" ? PTT_UNAVAILABLE : err.message);
        });
    };
    const onUp = (e: KeyboardEvent) => {
      if (e.code !== "Space" && e.key !== " ") return;
      release();
    };
    const onAway = () => release();
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);
    window.addEventListener("blur", onAway);
    document.addEventListener("visibilitychange", onAway);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup", onUp);
      window.removeEventListener("blur", onAway);
      document.removeEventListener("visibilitychange", onAway);
      release();
      if (timer.current) clearTimeout(timer.current);
    };
  }, [release, say]);

  return { listening, hint };
}

/**
 * Hold-to-talk for the Live page: starts a voice session for the page's DE (the first one with a
 * seat unless one is picked) and the first board, through `startVoiceSession`.
 */
export function useLivePushToTalk({
  api,
  company,
  sessionId,
  deId,
  enabled,
  deps,
}: {
  api: Api;
  company: CompanyAccess;
  sessionId: string;
  deId?: string | undefined;
  enabled: boolean;
  deps?: VoiceDeps;
}): PushToTalk {
  const co = company.id;
  const { ready } = useVoiceReady(api, company);
  const { seated } = useSeatedDes(api, co);
  const boards = useQuery({
    queryKey: ["voice-panel", co, "boards"],
    queryFn: () => api.client.boards.list({ params: { co }, query: { limit: 200 } }),
  });
  const de = seated.find((d) => d.membership_id === deId) ?? seated[0] ?? null;
  const board = boards.data?.data[0] ?? null;
  const ptt = usePushToTalk({
    enabled,
    ready,
    begin: async () => {
      if (!de?.seat || !board)
        throw new VoiceStartError(
          "no_target",
          "Voice needs a digital employee with a seat and a board.",
        );
      return startVoiceSession(
        {
          companyId: co,
          sessionId,
          deMembershipId: de.membership_id,
          deSeatId: de.seat.id,
          deName: de.display_name,
          boardId: board.id,
        },
        deps,
      );
    },
  });
  return ptt;
}
